import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CalcConfigService } from '_services/calc-config.service';
import { Table, applyNewMatrixTableLayout, applyStandardTableLayout } from '../../_models/table';
import { validateMatrixTable } from '../../_models/table-matrix.validation';
import { filter as lodashFilter } from 'lodash-es';
import { combineLatest, Subscription } from 'rxjs';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';

import { AidYearComponent } from '../../_shared/aid-year/aid-year.component';
import { TableComponent } from '../table/table.component';
import { KeyboardScrollableDirective } from '../../_shared/directives/keyboard-scrollable.directive';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';

@Component({
    selector: 'app-table-detail',
    templateUrl: './table-detail.component.html',
    styleUrls: ['./table-detail.component.css'],
    imports: [
        MatButton,
        MatIcon,
        MatCard,
        MatCardHeader,
        MatCardTitle,
        MatCardContent,
        AidYearComponent,
        TableComponent,
        KeyboardScrollableDirective,
    ],
})
export class TableDetailComponent implements OnInit, OnDestroy {
  table: Table | null = null;
  tableCopy: Table | null = null;
  editMode = false;
  isNew = false;
  /** New-table wizard: pick layout first, then the full editor (`null` when not creating). */
  newTableWizardStep: 'layout' | 'details' | null = null;
  /**
   * One-shot: after “Advanced matrix” on the new-table wizard, open matrix row/column input editing once.
   * Cleared when `TableComponent` emits `openRowColumnInputStepConsumed`.
   */
  openRowColumnInputStepOnce = false;
  notFound = false;
  loading = true;
  origin: string | null = null;
  private subscription?: Subscription;
  private schoolId = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public cc: CalcConfigService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.schoolId = this.route.parent?.parent?.snapshot.paramMap.get('schoolId') ?? '';
    const navState = history.state as { from?: string; };
    this.origin = this.normalizeOrigin(navState?.from);
    this.subscription = combineLatest([this.route.paramMap, this.route.queryParamMap, this.cc.tables]).subscribe(([params, queryParams, tables]) => {
      this.setOriginFromParams(queryParams);
      this.resolveTable(params, tables);
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  startEdit(): void {
    if (!this.table) {
      return;
    }
    this.tableCopy = this.cloneTable(this.table);
    this.editMode = true;
  }

  save(): void {
    if (!this.tableCopy) {
      return;
    }
    if (this.tableCopy.matrixTable) {
      const errors = validateMatrixTable(this.tableCopy.matrixTable);
      if (errors.length > 0) {
        this.snackBar.open(errors.join(' — '), 'Dismiss', { duration: 16_000 });
        return;
      }
    }
    const toSave = this.cloneTable(this.tableCopy);
    this.cc.saveTable(this.schoolId, toSave);
    this.table = toSave;
    this.tableCopy = this.cloneTable(toSave);
    this.editMode = false;
    if (this.isNew) {
      this.router.navigate(['../', toSave.tableId], { relativeTo: this.route });
    }
  }

  cancel(): void {
    if (this.isNew) {
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }
    if (this.table) {
      this.tableCopy = this.cloneTable(this.table);
    }
    this.editMode = false;
  }

  confirmDelete(): void {
    if (!this.table) {
      return;
    }

    const usage = lodashFilter(this.cc.usedBy.value, { entityId: this.table.tableId, entityType: 'Table' });
    if (usage.length > 0) {
      alert('This table is being used. You need to remove uses before deleting the table');
      return;
    }

    const confirmed = confirm(`Delete "${this.table.name}"?`);
    if (!confirmed) {
      return;
    }

    this.cc.deleteTable(this.schoolId, this.table);
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  goBack(): void {
    const destination = this.origin ?? 'tables';
    this.router.navigate(['/', this.schoolId, destination]);
  }

  get activeTableName(): string {
    if (this.editMode) {
      return this.tableCopy?.name || 'New Table';
    }
    return this.table?.name || 'New Table';
  }

  private resolveTable(params: ParamMap, tables: Table[]): void {
    const tableId = params.get('tableId');
    if (!tableId) {
      if (!this.isNew) {
        this.initializeNewTable();
      }
      this.loading = false;
      this.notFound = false;
      return;
    }

    this.isNew = false;
    this.newTableWizardStep = null;
    this.openRowColumnInputStepOnce = false;
    const table = tables.find(t => t.tableId === tableId);

    if (!table) {
      this.table = null;
      this.tableCopy = null;
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.notFound = false;
    this.table = table;
    if (!this.editMode) {
      this.tableCopy = this.cloneTable(table);
    }
    this.loading = false;
  }

  private initializeNewTable(): void {
    const newTable: Table = {
      tableId: this.cc.getUniqueId(),
      name: 'New Table',
      description: '',
      type: 'Question',
      rangeTest: true
    };

    this.table = newTable;
    this.tableCopy = this.cloneTable(newTable);
    this.isNew = true;
    this.editMode = true;
    this.newTableWizardStep = 'layout';
    this.openRowColumnInputStepOnce = false;
  }

  onNewTableStandardLayout(): void {
    if (!this.tableCopy) {
      return;
    }
    applyStandardTableLayout(this.tableCopy);
    this.newTableWizardStep = 'details';
    this.openRowColumnInputStepOnce = false;
  }

  onNewTableMatrixLayout(): void {
    if (!this.tableCopy) {
      return;
    }
    applyNewMatrixTableLayout(this.tableCopy);
    this.newTableWizardStep = 'details';
    this.openRowColumnInputStepOnce = true;
  }

  onOpenRowColumnInputStepConsumed(): void {
    this.openRowColumnInputStepOnce = false;
  }

  private cloneTable(table: Table): Table {
    return JSON.parse(JSON.stringify(table));
  }

  get backLinkLabel(): string {
    const destination = this.origin ?? 'tables';
    return `Back to ${destination} `;
  }

  private setOriginFromParams(queryParams: ParamMap): void {
    const fromParam = queryParams.get('from');
    const normalized = this.normalizeOrigin(fromParam);
    if (normalized) {
      this.origin = normalized;
    }
  }

  private normalizeOrigin(origin?: string | null): string | null {
    if (typeof origin !== 'string') {
      return null;
    }
    const trimmed = origin.trim().toLowerCase();
    return trimmed.length > 0 ? trimmed : null;
  }
}
